const EVENT_NAME = "trekkin:rtdb-traffic";
const MAX_ENDPOINTS = 40;
const MAX_PATHS = 60;

const state = {
  startedAt: 0,
  initialized: false,
  lastError: "",
  wsConnectionsOpened: 0,
  wsConnectionsClosed: 0,
  wsActiveConnections: 0,
  wsInBytes: 0,
  wsOutBytes: 0,
  wsInMessages: 0,
  wsOutMessages: 0,
  fetchRequests: 0,
  fetchInBytes: 0,
  fetchOutBytes: 0,
  xhrRequests: 0,
  xhrInBytes: 0,
  xhrOutBytes: 0,
  logicalSnapshots: 0,
  logicalBytes: 0,
  activeListeners: 0,
  listenerSeq: 0,
  listenersByPath: {},
  endpoints: {},
  paths: {},
  minuteBuckets: {},
  transportEnabled: {
    websocket: false,
    fetch: false,
    xhr: false,
  },
};

const subscribers = new Set();
let emitTimer = null;
const patchState = {
  websocket: false,
  fetch: false,
  xhr: false,
};

function cloneSimple(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function appendError(message) {
  const next = String(message || "").trim();
  if (!next) return;
  if (!state.lastError) {
    state.lastError = next;
    return;
  }
  const parts = new Set(
    String(state.lastError)
      .split(" | ")
      .map((item) => item.trim())
      .filter(Boolean)
  );
  parts.add(next);
  state.lastError = Array.from(parts).join(" | ");
}

function replaceWindowProperty(key, value) {
  try {
    window[key] = value;
    return window[key] === value;
  } catch {}

  try {
    const desc = Object.getOwnPropertyDescriptor(window, key);
    if (desc?.configurable) {
      Object.defineProperty(window, key, {
        configurable: true,
        writable: true,
        value,
      });
      return window[key] === value;
    }
  } catch {}

  return false;
}

function nowMinuteKey(ts = Date.now()) {
  return Math.floor(ts / 60000);
}

function cleanupBuckets() {
  const current = nowMinuteKey();
  Object.keys(state.minuteBuckets).forEach((key) => {
    if (Number(key) < current - 180) {
      delete state.minuteBuckets[key];
    }
  });
}

function touchMinute(direction, bytes) {
  const key = nowMinuteKey();
  const bucket = state.minuteBuckets[key] || {
    inBytes: 0,
    outBytes: 0,
    wsInBytes: 0,
    wsOutBytes: 0,
    fetchInBytes: 0,
    fetchOutBytes: 0,
    xhrInBytes: 0,
    xhrOutBytes: 0,
  };
  if (direction === "in") bucket.inBytes += bytes;
  if (direction === "out") bucket.outBytes += bytes;
  state.minuteBuckets[key] = bucket;
  cleanupBuckets();
}

function emitSoon() {
  if (emitTimer) return;
  emitTimer = window.setTimeout(() => {
    emitTimer = null;
    const snapshot = getRtdbTrafficSnapshot();
    try {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: snapshot }));
    } catch {}
    subscribers.forEach((cb) => {
      try {
        cb(snapshot);
      } catch {}
    });
  }, 250);
}

function textBytes(value) {
  try {
    return new TextEncoder().encode(String(value || "")).length;
  } catch {
    return String(value || "").length;
  }
}

function estimateBytes(payload) {
  if (payload == null) return 0;
  if (typeof payload === "string") return textBytes(payload);
  if (payload instanceof ArrayBuffer) return payload.byteLength || 0;
  if (ArrayBuffer.isView(payload)) return payload.byteLength || 0;
  if (typeof Blob !== "undefined" && payload instanceof Blob) return payload.size || 0;
  if (typeof payload === "object") {
    try {
      return textBytes(JSON.stringify(payload));
    } catch {
      return 0;
    }
  }
  return 0;
}

function isRtdbUrl(raw) {
  try {
    const url = new URL(String(raw || ""), window.location?.origin || "https://localhost");
    return (
      /(?:^|\.)firebasedatabase\.app$/i.test(url.hostname) ||
      /(?:^|\.)firebaseio\.com$/i.test(url.hostname)
    );
  } catch {
    const value = String(raw || "");
    return value.includes("firebasedatabase.app") || value.includes("firebaseio.com");
  }
}

function endpointKey(raw) {
  try {
    const url = new URL(String(raw || ""), window.location?.origin || "https://localhost");
    const path = `${url.hostname}${url.pathname}`;
    return path || String(raw || "unknown");
  } catch {
    return String(raw || "unknown");
  }
}

function trimObject(obj, limit) {
  const entries = Object.entries(obj || {}).sort((a, b) => {
    const aValue = Number(a[1]?.inBytes || 0) + Number(a[1]?.outBytes || 0);
    const bValue = Number(b[1]?.inBytes || 0) + Number(b[1]?.outBytes || 0);
    return bValue - aValue;
  });
  const trimmed = entries.slice(0, limit);
  return Object.fromEntries(trimmed);
}

function bumpEndpoint(url, direction, bytes, transport) {
  const key = endpointKey(url);
  const prev = state.endpoints[key] || {
    inBytes: 0,
    outBytes: 0,
    hits: 0,
    transport: transport || "",
    lastAt: 0,
  };
  prev.hits += 1;
  prev.transport = transport || prev.transport;
  prev.lastAt = Date.now();
  if (direction === "in") prev.inBytes += bytes;
  if (direction === "out") prev.outBytes += bytes;
  state.endpoints[key] = prev;
  state.endpoints = trimObject(state.endpoints, MAX_ENDPOINTS);
}

function bumpPath(path, direction, bytes, meta = {}) {
  const safePath = String(path || "(unknown)");
  const prev = state.paths[safePath] || {
    inBytes: 0,
    outBytes: 0,
    hits: 0,
    lastAt: 0,
    source: meta.source || "",
    itemsCount: 0,
  };
  prev.hits += 1;
  prev.lastAt = Date.now();
  if (meta.source) prev.source = meta.source;
  if (Number.isFinite(meta.itemsCount)) prev.itemsCount = meta.itemsCount;
  if (direction === "in") prev.inBytes += bytes;
  if (direction === "out") prev.outBytes += bytes;
  state.paths[safePath] = prev;
  state.paths = trimObject(state.paths, MAX_PATHS);
}

function safeParseJson(raw) {
  if (typeof raw !== "string") return null;
  const txt = raw.trim();
  if (!txt || (txt[0] !== "{" && txt[0] !== "[")) return null;
  try {
    return JSON.parse(txt);
  } catch {
    return null;
  }
}

function extractWirePath(data, direction) {
  const parsed = safeParseJson(data);
  if (!parsed || typeof parsed !== "object") return null;
  const body = parsed?.d?.b;
  const action = parsed?.d?.a;
  if (!body || typeof body !== "object") return null;
  const path =
    body.p ||
    body.path ||
    body.q?.p ||
    body.b?.p ||
    null;
  if (!path) return null;
  const payload =
    direction === "in"
      ? body.d ?? body.data ?? body.m ?? null
      : body.d ?? body.data ?? null;
  return {
    action: String(action || ""),
    path: String(path || ""),
    payloadBytes: estimateBytes(payload),
  };
}

function registerBytes({ url, direction, bytes, transport, wireData = null }) {
  if (!Number.isFinite(bytes) || bytes <= 0) return;
  touchMinute(direction, bytes);
  bumpEndpoint(url, direction, bytes, transport);
  const wire = extractWirePath(wireData, direction);
  if (wire?.path) {
    bumpPath(wire.path, direction, wire.payloadBytes || bytes, {
      source: `wire:${transport}`,
    });
  }
  emitSoon();
}

function patchWebSocket() {
  if (typeof window === "undefined" || typeof window.WebSocket !== "function") return;
  if (patchState.websocket || window.WebSocket.__trekkinRtdbPatched) {
    state.transportEnabled.websocket = true;
    return;
  }

  const NativeWebSocket = window.WebSocket;

  class TrekkinTrackedWebSocket extends NativeWebSocket {
    constructor(url, protocols) {
      super(url, protocols);
      const tracked = isRtdbUrl(url);
      this.__trekkinRtdb = tracked ? { url: String(url || "") } : null;

      if (tracked) {
        state.wsConnectionsOpened += 1;
        state.wsActiveConnections += 1;
        emitSoon();

        this.addEventListener("message", (event) => {
          const bytes = estimateBytes(event?.data);
          state.wsInBytes += bytes;
          state.wsInMessages += 1;
          const key = nowMinuteKey();
          const bucket = state.minuteBuckets[key] || {};
          bucket.wsInBytes = Number(bucket.wsInBytes || 0) + bytes;
          state.minuteBuckets[key] = bucket;
          registerBytes({
            url,
            direction: "in",
            bytes,
            transport: "websocket",
            wireData: typeof event?.data === "string" ? event.data : null,
          });
        });

        this.addEventListener("close", () => {
          state.wsConnectionsClosed += 1;
          state.wsActiveConnections = Math.max(0, state.wsActiveConnections - 1);
          emitSoon();
        });
      }
    }

    send(data) {
      if (this.__trekkinRtdb?.url) {
        const bytes = estimateBytes(data);
        state.wsOutBytes += bytes;
        state.wsOutMessages += 1;
        const key = nowMinuteKey();
        const bucket = state.minuteBuckets[key] || {};
        bucket.wsOutBytes = Number(bucket.wsOutBytes || 0) + bytes;
        state.minuteBuckets[key] = bucket;
        registerBytes({
          url: this.__trekkinRtdb.url,
          direction: "out",
          bytes,
          transport: "websocket",
          wireData: typeof data === "string" ? data : null,
        });
      }
      return super.send(data);
    }
  }

  TrekkinTrackedWebSocket.__trekkinRtdbPatched = true;
  const replaced = replaceWindowProperty("WebSocket", TrekkinTrackedWebSocket);
  if (!replaced) {
    appendError("WebSocket patch: readonly property");
    return;
  }
  patchState.websocket = true;
  state.transportEnabled.websocket = true;
}

function patchFetch() {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  if (patchState.fetch || window.fetch.__trekkinRtdbPatched) {
    state.transportEnabled.fetch = true;
    return;
  }
  const nativeFetch = window.fetch.bind(window);

  const trackedFetch = async (...args) => {
    const input = args[0];
    const init = args[1] || {};
    const url =
      typeof input === "string"
        ? input
        : input?.url || "";

    const tracked = isRtdbUrl(url);
    const bodyBytes = tracked ? estimateBytes(init?.body ?? input?.body ?? null) : 0;

    if (tracked) {
      state.fetchRequests += 1;
      state.fetchOutBytes += bodyBytes;
      const key = nowMinuteKey();
      const bucket = state.minuteBuckets[key] || {};
      bucket.fetchOutBytes = Number(bucket.fetchOutBytes || 0) + bodyBytes;
      state.minuteBuckets[key] = bucket;
      registerBytes({
        url,
        direction: "out",
        bytes: bodyBytes,
        transport: "fetch",
      });
    }

    const res = await nativeFetch(...args);

    if (tracked) {
      const lenHeader = Number(res.headers?.get("content-length") || 0);
      const inBytes = Number.isFinite(lenHeader) && lenHeader > 0 ? lenHeader : 0;
      state.fetchInBytes += inBytes;
      const key = nowMinuteKey();
      const bucket = state.minuteBuckets[key] || {};
      bucket.fetchInBytes = Number(bucket.fetchInBytes || 0) + inBytes;
      state.minuteBuckets[key] = bucket;
      registerBytes({
        url,
        direction: "in",
        bytes: inBytes,
        transport: "fetch",
      });
    }

    return res;
  };

  trackedFetch.__trekkinRtdbPatched = true;
  const replaced = replaceWindowProperty("fetch", trackedFetch);
  if (!replaced) {
    appendError("Fetch patch: readonly property");
    return;
  }
  patchState.fetch = true;
  state.transportEnabled.fetch = true;
}

function patchXhr() {
  if (typeof window === "undefined" || typeof window.XMLHttpRequest !== "function") return;
  if (patchState.xhr || window.XMLHttpRequest.__trekkinRtdbPatched) {
    state.transportEnabled.xhr = true;
    return;
  }

  const proto = window.XMLHttpRequest.prototype;
  const nativeOpen = proto.open;
  const nativeSend = proto.send;

  try {
    proto.open = function patchedOpen(method, url, ...rest) {
      this.__trekkinRtdb = {
        method: String(method || "GET").toUpperCase(),
        url: String(url || ""),
        tracked: isRtdbUrl(url),
      };
      return nativeOpen.call(this, method, url, ...rest);
    };

    proto.send = function patchedSend(body) {
      const meta = this.__trekkinRtdb || null;
      if (meta?.tracked) {
        const outBytes = estimateBytes(body);
        state.xhrRequests += 1;
        state.xhrOutBytes += outBytes;
        const key = nowMinuteKey();
        const bucket = state.minuteBuckets[key] || {};
        bucket.xhrOutBytes = Number(bucket.xhrOutBytes || 0) + outBytes;
        state.minuteBuckets[key] = bucket;
        registerBytes({
          url: meta.url,
          direction: "out",
          bytes: outBytes,
          transport: "xhr",
        });

        this.addEventListener(
          "loadend",
          () => {
            const responseBytes =
              Number(this.getResponseHeader?.("content-length") || 0) ||
              estimateBytes(this.responseText || this.response || null);
            state.xhrInBytes += responseBytes;
            const bucketKey = nowMinuteKey();
            const localBucket = state.minuteBuckets[bucketKey] || {};
            localBucket.xhrInBytes = Number(localBucket.xhrInBytes || 0) + responseBytes;
            state.minuteBuckets[bucketKey] = localBucket;
            registerBytes({
              url: meta.url,
              direction: "in",
              bytes: responseBytes,
              transport: "xhr",
            });
          },
          { once: true }
        );
      }

      return nativeSend.call(this, body);
    };

    patchState.xhr = true;
    state.transportEnabled.xhr = true;
    try {
      window.XMLHttpRequest.__trekkinRtdbPatched = true;
    } catch {}
  } catch (err) {
    appendError(`XHR patch: ${err?.message || String(err || "failed")}`);
  }
}

export function startRtdbTrafficMonitor() {
  if (typeof window === "undefined") return;
  if (state.initialized) return;
  const startedAt = Date.now();
  state.lastError = "";
  try {
    patchWebSocket();
  } catch (err) {
    appendError(`WebSocket patch: ${err?.message || String(err || "failed")}`);
  }
  try {
    patchFetch();
  } catch (err) {
    appendError(`Fetch patch: ${err?.message || String(err || "failed")}`);
  }
  try {
    patchXhr();
  } catch (err) {
    appendError(`XHR patch: ${err?.message || String(err || "failed")}`);
  }
  state.startedAt = startedAt;
  state.initialized = true;
  window.__trekkin_rtdb_traffic = {
    getSnapshot: getRtdbTrafficSnapshot,
    reset: resetRtdbTrafficMonitor,
    subscribe: subscribeRtdbTraffic,
  };
  emitSoon();
}

export function getRtdbTrafficSnapshot() {
  const totalIn =
    state.wsInBytes + state.fetchInBytes + state.xhrInBytes;
  const totalOut =
    state.wsOutBytes + state.fetchOutBytes + state.xhrOutBytes;
  const minutes = Object.entries(state.minuteBuckets)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([minute, data]) => ({
      minute: Number(minute),
      ...(data || {}),
    }));

  return cloneSimple({
    startedAt: state.startedAt,
    initialized: state.initialized,
    totalInBytes: totalIn,
    totalOutBytes: totalOut,
    wsConnectionsOpened: state.wsConnectionsOpened,
    wsConnectionsClosed: state.wsConnectionsClosed,
    wsActiveConnections: state.wsActiveConnections,
    wsInBytes: state.wsInBytes,
    wsOutBytes: state.wsOutBytes,
    wsInMessages: state.wsInMessages,
    wsOutMessages: state.wsOutMessages,
    fetchRequests: state.fetchRequests,
    fetchInBytes: state.fetchInBytes,
    fetchOutBytes: state.fetchOutBytes,
    xhrRequests: state.xhrRequests,
    xhrInBytes: state.xhrInBytes,
    xhrOutBytes: state.xhrOutBytes,
    logicalSnapshots: state.logicalSnapshots,
    logicalBytes: state.logicalBytes,
    activeListeners: state.activeListeners,
    listenersByPath: state.listenersByPath,
    endpoints: state.endpoints,
    paths: state.paths,
    minuteBuckets: minutes,
    transportEnabled: state.transportEnabled,
    lastError: state.lastError,
  });
}

export function subscribeRtdbTraffic(callback) {
  if (typeof callback !== "function") return () => {};
  subscribers.add(callback);
  try {
    callback(getRtdbTrafficSnapshot());
  } catch {}
  return () => subscribers.delete(callback);
}

export function resetRtdbTrafficMonitor() {
  const keep = {
    initialized: state.initialized,
    transportEnabled: state.transportEnabled,
    lastError: "",
  };
  Object.keys(state).forEach((key) => {
    if (key in keep) return;
    if (typeof state[key] === "number") state[key] = 0;
    else if (Array.isArray(state[key])) state[key] = [];
    else if (state[key] && typeof state[key] === "object") state[key] = {};
  });
  state.startedAt = Date.now();
  state.initialized = keep.initialized;
  state.transportEnabled = keep.transportEnabled;
  emitSoon();
}

export function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let current = bytes;
  let idx = 0;
  while (current >= 1024 && idx < units.length - 1) {
    current /= 1024;
    idx += 1;
  }
  return `${current.toFixed(current >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}

export function resolveRtdbRefPath(queryRef) {
  if (!queryRef) return "(unknown)";
  try {
    if (typeof queryRef.toString === "function") {
      const url = new URL(queryRef.toString());
      return url.pathname || "(unknown)";
    }
  } catch {}
  try {
    const pieces = queryRef?._path?.pieces_ || queryRef?._path?.pieces || null;
    if (Array.isArray(pieces)) return `/${pieces.join("/")}`;
  } catch {}
  return "(unknown)";
}

export function trackRtdbLogicalSnapshot({ path, payload, itemsCount = null, source = "logical" }) {
  const bytes = estimateBytes(payload);
  state.logicalSnapshots += 1;
  state.logicalBytes += bytes;
  bumpPath(path || "(unknown)", "in", bytes, {
    source,
    itemsCount: Number.isFinite(itemsCount) ? itemsCount : null,
  });
  emitSoon();
}

export function trackRtdbListenerOpen(path) {
  const safePath = String(path || "(unknown)");
  state.listenerSeq += 1;
  state.activeListeners += 1;
  state.listenersByPath[safePath] = Number(state.listenersByPath[safePath] || 0) + 1;
  emitSoon();
  return `${safePath}#${state.listenerSeq}`;
}

export function trackRtdbListenerClose(token) {
  if (!token) return;
  const safePath = String(token).split("#")[0] || "(unknown)";
  state.activeListeners = Math.max(0, state.activeListeners - 1);
  const next = Math.max(0, Number(state.listenersByPath[safePath] || 0) - 1);
  if (next <= 0) delete state.listenersByPath[safePath];
  else state.listenersByPath[safePath] = next;
  emitSoon();
}
